import { VesselProductsGetResponse } from "src/model/api/response/VesselProductsGetResponse";

import {
  GridRecommendProductType,
  MiddleType,
} from "../../component/lcm/RepairSchedule";
import { OrderStatus } from "../../util/Constant";
import { TIMINGS, TIMING_SUBS } from "./LcmConst";

/** 実施年度 1 つ分の列の値 */
type TimingColumns = {
  recommend: string;
  done: string | undefined;
  quotation: boolean | undefined;
  recommendPrice: number | undefined;
  recommendSubtotal: number | undefined;
  doneSubtotal: number | undefined;
  quotationSubtotal: number | undefined;
  quotationStatusClassValue: string | undefined;
  orderStatusClassValue: string | undefined;
};

/**
 * 実施年度ごとの列（recommend1, done1, ... recommend30, ...）を組み立てる
 * @param columnsOf 実施年度から列の値を求める関数
 */
function buildTimingColumns(
  columnsOf: (timing: number) => TimingColumns
): Partial<GridRecommendProductType> {
  const columns: Record<string, unknown> = {};
  TIMINGS.forEach((timing) => {
    const values = columnsOf(timing);
    columns[`recommend${timing}`] = values.recommend;
    columns[`done${timing}`] = values.done;
    columns[`quotation${timing}`] = values.quotation;
    columns[`recommendPrice${timing}`] = values.recommendPrice;
    columns[`recommendSubtotal${timing}`] = values.recommendSubtotal;
    columns[`doneSubtotal${timing}`] = values.doneSubtotal;
    columns[`quotationSubtotal${timing}`] = values.quotationSubtotal;
    columns[`quotationStatusClassValue${timing}`] =
      values.quotationStatusClassValue;
    columns[`orderStatusClassValue${timing}`] = values.orderStatusClassValue;
  });
  return columns as Partial<GridRecommendProductType>;
}

// 部品交換内容グリッドデータ取得
export function getGridRecommenProductList(
  vesselProductsGetResponse: VesselProductsGetResponse[],
  yearCheckValue: number
) {
  const vesselProductData: GridRecommendProductType[] =
    vesselProductsGetResponse.map((it: VesselProductsGetResponse) => {
      const subs = it.vesselProductsGetResponseSub;

      // 実施予定かどうか
      // 年チェックが 0 のとき、2.5年単位の年度（TIMING_SUBS）は翌年度の予定も含めて判定する
      const isRecommended = (timing: number) => {
        const sub = subs[timing - 1];
        return yearCheckValue === 0 && TIMING_SUBS.includes(timing)
          ? sub.inspectionYearFlg || subs[timing].inspectionYearFlg
          : sub.inspectionYearFlg;
      };

      return {
        rowId: it.detailNo.toString(),
        display: undefined,
        largeClassName: it.largeClassName,
        middleClassName: it.middleClassName,
        discontinuedFlg: it.discontinuedFlg,
        replacementFlg: it.replacementFlg,
        productCode: it.productCode,
        productGroupName: it.productGroupName,
        spec: it.spec,
        recommendQuantity: it.recommendQuantity,
        ...buildTimingColumns((timing) => {
          const sub = subs[timing - 1];
          const recommended = isRecommended(timing);
          return {
            recommend: recommended ? "●" : "",
            done:
              sub.orderReplyDetailSubTotal &&
              sub.orderStatusClassValue === OrderStatus.COMPLETED
                ? "●"
                : "",
            quotation: sub.quotationReplyDetailSubTotal ? true : false,
            recommendPrice: sub.recommendDetailSubTotal,
            recommendSubtotal: recommended
              ? sub.recommendDetailSubTotal
              : undefined,
            doneSubtotal:
              sub.orderStatusClassValue === OrderStatus.COMPLETED
                ? sub.orderReplyDetailSubTotal
                : undefined,
            quotationSubtotal: sub.quotationReplyDetailSubTotal,
            quotationStatusClassValue: sub.quotationStatusClassValue,
            orderStatusClassValue: sub.orderStatusClassValue,
          };
        }),
      } as GridRecommendProductType;
    });
  return vesselProductData;
}

/** 金額列を合計する */
function sumColumn(
  rows: GridRecommendProductType[],
  key: keyof GridRecommendProductType
) {
  return rows.reduce((a, x) => {
    return a + ((x[key] as number | undefined) ?? 0);
  }, 0);
}

/**
 * 明細行を集計した行（大分類・中分類・合計）を作る
 * 金額列（予定・実施・見積）は rows の合計、それ以外の年度列は空にする
 */
function createSummaryRow(
  rows: GridRecommendProductType[],
  header: Pick<
    GridRecommendProductType,
    "rowId" | "display" | "largeClassName" | "middleClassName"
  >
) {
  return {
    ...header,
    discontinuedFlg: undefined,
    replacementFlg: undefined,
    productCode: undefined,
    productGroupName: undefined,
    spec: undefined,
    recommendQuantity: undefined,
    ...buildTimingColumns((timing) => ({
      recommend: "",
      done: "",
      quotation: undefined,
      recommendPrice: undefined,
      recommendSubtotal: sumColumn(
        rows,
        `recommendSubtotal${timing}` as keyof GridRecommendProductType
      ),
      doneSubtotal: sumColumn(
        rows,
        `doneSubtotal${timing}` as keyof GridRecommendProductType
      ),
      quotationSubtotal: sumColumn(
        rows,
        `quotationSubtotal${timing}` as keyof GridRecommendProductType
      ),
      quotationStatusClassValue: undefined,
      orderStatusClassValue: undefined,
    })),
  } as GridRecommendProductType;
}

// 大分類行データ取得
export function getGridRecommenProductLargeClassList(
  gridRecommendProductList: GridRecommendProductType[]
) {
  const largeClassGroup: string[] = [];
  gridRecommendProductList.forEach((element) => {
    if (
      element.largeClassName &&
      !largeClassGroup.includes(element.largeClassName)
    ) {
      largeClassGroup.push(element.largeClassName);
    }
  });

  const result: GridRecommendProductType[] = [];
  largeClassGroup.forEach((largName) => {
    const filterData: GridRecommendProductType[] =
      gridRecommendProductList.filter((it) => it.largeClassName === largName);
    result.push(
      createSummaryRow(filterData, {
        rowId: largName,
        display: "LARGE",
        largeClassName: largName,
        middleClassName: undefined,
      })
    );
  });
  return result;
}

// 中分類行データ取得
export function getGridRecommenProductMiddleClassList(
  gridRecommendProductList: GridRecommendProductType[]
) {
  const middleClassGroup: MiddleType[] = [];
  gridRecommendProductList.forEach((element) => {
    const checkData: MiddleType = {
      largeName: element.largeClassName,
      middleName: element.middleClassName,
    };
    if (
      !middleClassGroup.find((middle) => {
        return (
          middle.largeName === checkData.largeName &&
          middle.middleName === checkData.middleName
        );
      })
    ) {
      middleClassGroup.push(checkData);
    }
  });

  const result: GridRecommendProductType[] = [];
  middleClassGroup.forEach((middle) => {
    const filterData: GridRecommendProductType[] =
      gridRecommendProductList.filter(
        (it) =>
          it.middleClassName === middle.middleName &&
          it.largeClassName === middle.largeName
      );
    result.push(
      createSummaryRow(filterData, {
        rowId: middle.middleName,
        display: "MIDDLE",
        largeClassName: middle.largeName,
        middleClassName: middle.middleName,
      })
    );
  });
  return result;
}

// 合計行データ取得
export function getGridRecommenProductSumList(
  gridRecommendProductList: GridRecommendProductType[],
  summaryTitle: string
) {
  return [
    createSummaryRow(gridRecommendProductList, {
      rowId: "SUM",
      display: "SUM",
      largeClassName: summaryTitle,
      middleClassName: undefined,
    }),
  ];
}
